import { useEffect, useLayoutEffect, useRef } from "react";

/**
 * Draws the bid/ask depth bars for the SuperDOM ladder onto a single canvas
 * layer behind the table, replacing a per-<tr> CSS linear-gradient that the
 * browser had to re-rasterize on every book tick (the measured paint hotpath).
 *
 * The canvas is an absolutely-positioned, non-interactive sibling inside the
 * scroll container, sized to the whole table so it scrolls with the rows for
 * free (no scroll listener). Bar geometry is computed analytically
 * (headerH + i * rowH) — never a per-row getBoundingClientRect, which would
 * reintroduce the reflow storm we are removing. Draws are coalesced to one
 * requestAnimationFrame per frame.
 *
 * Interaction is untouched: the canvas has pointer-events:none and sits behind
 * the DOM table (z-index 0), so hit-testing, hover, drag and the order/bracket
 * layers keep working exactly as before.
 */
export function useDepthBarCanvas({
  canvasRef,
  tableRef,
  rows,
  maxQty,
  bidColor,
  askColor,
}) {
  const rafRef = useRef(0);
  // Latest data for the rAF/ResizeObserver callbacks without re-subscribing.
  const stateRef = useRef({ rows, maxQty });
  stateRef.current = { rows, maxQty };

  const draw = () => {
    const canvas = canvasRef.current;
    const table = tableRef.current;
    if (!canvas || !table) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const cssW = table.offsetWidth;
    const cssH = table.offsetHeight;
    if (!cssW || !cssH) return;

    const dpr = window.devicePixelRatio || 1;
    const bw = Math.round(cssW * dpr);
    const bh = Math.round(cssH * dpr);
    if (canvas.width !== bw || canvas.height !== bh) {
      canvas.width = bw;
      canvas.height = bh;
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    const { rows: r, maxQty: mq } = stateRef.current;
    const tbody = table.tBodies[0];
    if (!mq || !tbody || !tbody.rows.length) return;

    // Derive row geometry from the live (fractional, scroll-invariant) layout:
    // offsetHeight rounds to an integer, so headerH + i * rowH would drift ~1px
    // per row down the ladder. A few getBoundingClientRect reads, once per draw.
    const domRows = tbody.rows;
    const tableTop = table.getBoundingClientRect().top;
    const firstTop = domRows[0].getBoundingClientRect().top - tableTop;
    const rowH =
      domRows.length > 1
        ? domRows[1].getBoundingClientRect().top -
          domRows[0].getBoundingClientRect().top
        : domRows[0].getBoundingClientRect().height;
    const half = cssW / 2;
    const n = Math.min(r.length, domRows.length);

    for (let i = 0; i < n; i++) {
      const row = r[i];
      const top = firstTop + i * rowH;
      const bidQty = row.bid?.quantity;
      if (bidQty) {
        const w = (bidQty / mq) * half;
        ctx.fillStyle = bidColor;
        ctx.fillRect(half - w, top, w, rowH);
      }
      const askQty = row.ask?.quantity;
      if (askQty) {
        const w = (askQty / mq) * half;
        ctx.fillStyle = askColor;
        ctx.fillRect(half, top, w, rowH);
      }
    }
  };

  const schedule = () => {
    if (rafRef.current) return; // a frame is already pending — coalesce into it
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      draw();
    });
  };

  // Redraw whenever the ladder data or scale changes.
  useLayoutEffect(() => {
    schedule();
  }, [rows, maxQty]);

  // Redraw on table/container resize (row geometry changes) + cancel on unmount.
  useEffect(() => {
    const table = tableRef.current;
    if (!table || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => schedule());
    ro.observe(table);
    return () => {
      ro.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);
}
